/**
 * Google Review Audit → email-generation context.
 *
 * The audit lives on the contact at researchIntelligence.googleReviewAudit
 * (written by googleReviewAudit / the outreach agent step). Mail prompts get a
 * trimmed copy — no raw review texts — plus a tact rule, because a cold email
 * that quotes someone's 1-star reviews back at them reads as an attack.
 *
 * Twins: functions/helpers/reviewAuditContext.js and src/shared/reviewAuditContext.js
 * must stay identical except for the export line.
 */

const clip = (v, max) => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const strings = (list, max, len) => (Array.isArray(list) ? list : [])
  .map((x) => clip(typeof x === 'string' ? x : x?.theme, len))
  .filter(Boolean)
  .slice(0, max);

/** Prompt-sized copy of the stored audit; null when there is nothing usable. */
function reviewAuditForPrompt(audit) {
  if (!audit || typeof audit !== 'object' || !audit.place) return null;
  const place = audit.place || {};
  const stats = audit.stats || {};
  const a = audit.analysis || {};
  const complaints = (Array.isArray(a.complaints) ? a.complaints : [])
    .filter((c) => c && c.theme)
    .slice(0, 3)
    .map((c) => ({ theme: clip(c.theme, 80), mentions: num(c.mentions), severity: c.severity || null }));
  return {
    place: { name: clip(place.name, 120), rating: num(place.rating), reviewCount: num(place.reviewCount) },
    stats: {
      negativeShare: num(stats.negativeShare),
      ownerReplyRate: num(stats.ownerReplyRate),
      ownerReplyRateNegative: num(stats.ownerReplyRateNegative),
      trend: stats.trend || null,
    },
    analysis: {
      summary: clip(a.summary, 400),
      complaints,
      weaknesses: strings(a.weaknesses, 3, 160),
      strengths: strings(a.strengths, 3, 80),
      ownerResponse: clip(a.ownerResponse, 200),
      salesAngle: clip(a.salesAngle, 240),
      conversationStarters: strings(a.conversationStarters, 2, 200),
      customFindings: strings(a.customFindings, 3, 200),
    },
  };
}

/** Plain lines describing the audit (accepts the stored audit or its prompt copy). */
function reviewAuditLines(audit) {
  const r = reviewAuditForPrompt(audit);
  if (!r) return [];
  const { place, stats, analysis: a } = r;
  const lines = [];
  const head = [];
  if (place.rating != null) head.push(`${place.rating.toFixed(1)}★`);
  if (place.reviewCount != null) head.push(`${place.reviewCount} reviews`);
  if (stats.trend && stats.trend !== 'stable') head.push(`recent reviews ${stats.trend}`);
  if (head.length) lines.push(`Google rating: ${head.join(', ')}`);
  if (stats.ownerReplyRate != null) {
    const neg = stats.ownerReplyRateNegative != null ? ` (${stats.ownerReplyRateNegative}% of negative ones)` : '';
    lines.push(`Owner answers ${stats.ownerReplyRate}% of reviews${neg}`);
  }
  if (a.summary) lines.push(`Summary: ${a.summary}`);
  if (a.complaints.length) {
    lines.push(`Recurring complaints: ${a.complaints
      .map((c) => `${c.theme}${c.mentions != null ? ` (${c.mentions}×)` : ''}`)
      .join('; ')}`);
  }
  if (a.weaknesses.length) lines.push(`Fixable weak spots: ${a.weaknesses.join('; ')}`);
  if (a.strengths.length) lines.push(`Customers praise: ${a.strengths.join(', ')}`);
  if (a.ownerResponse) lines.push(`Owner replies: ${a.ownerResponse}`);
  if (a.customFindings.length) lines.push(`Specific findings: ${a.customFindings.join('; ')}`);
  if (a.salesAngle) lines.push(`Suggested angle: ${a.salesAngle}`);
  if (a.conversationStarters.length) lines.push(`Possible openers: ${a.conversationStarters.join(' | ')}`);
  return lines;
}

const REVIEW_TACT_RULE = 'Use tactfully: pick at most ONE pattern and phrase it as a neutral observation '
  + '("a few guests mention …"). Never quote a single review, never name a reviewer, never state the '
  + 'negative-review share, and never make the recipient feel exposed. Praise is a safer opener than criticism.';

/** Block for the standard (non-voice-clone) context string. '' when no audit. */
function formatReviewAuditContext(audit) {
  const lines = reviewAuditLines(audit);
  if (!lines.length) return '';
  return `\n[GOOGLE REVIEWS]\n${lines.join('\n')}\n${REVIEW_TACT_RULE}`;
}

export {
  reviewAuditForPrompt,
  reviewAuditLines,
  formatReviewAuditContext,
  REVIEW_TACT_RULE,
};
